import { IconButton, Menu, MenuItem, TableCell, TableRow } from '@material-ui/core';
import { FC, Fragment, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SnackbarUtils from 'utils/SnackbarUtils';
import styles from './ListaItem.scss';
import { getElementID, getMergedClassNames } from 'utils/HTMLUtils';
import { loadDadosEscolaByIdCoop, removePeriodoByIdCoopetiva } from 'store/modules/EscolasActions';
import { openModal } from 'store/modules/ModalActions';
import { RootState } from 'store';
import { ListaItemProps } from '../../../../__types';
import MoreVertIcon from 'components/Icons/MoreVertIcon';
import AccordionArrowOpenClose from 'assets/icons/AccordionArrowOpenClose';
import { TipoPrograma } from '../../../../__enum';
import { useHabilitaCampoPrograma } from '../../../../hooks/useDisablePrograma';
import ModalAviso from '../../../../components/Modais/ModalAviso';
import { useModalPeriodoPrograma } from '../../../../hooks/useModalProgramas';

const rootClassName = styles.rootClassName;

const ListaItem: FC<ListaItemProps> = ({ id, periodo, desabilitarDuplicar, paginaAtual, onRefresh }) => {
  const [opened, setOpened] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { classeCondicional, isDesabilitado } = useHabilitaCampoPrograma(TipoPrograma.CooperativaEscolar);
  const languageValues = useSelector((state: RootState) => (state.languages.languageValues));
  const { openModalFormDados } = useModalPeriodoPrograma();
  const dispatch = useDispatch();

  const toggleOpen = useCallback(() => setOpened((prev) => !prev), []);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getLabels = () => {
    return [
      null,
      languageValues.ensinoFundamentalIILabel,
      languageValues.ensinoMedioLabel,
      languageValues.classeEspecializadaLabel,
    ];
  }

  const getValueTypes = useCallback(() => {
    return [
      null,
      'ensFundII',
      'ensMedio',
      'especializada',
      'colaboradores'
    ];
  }, []);

  const getTotalByType = useCallback(() => {
    const valueTypes = getValueTypes();
    let totalProf = 0;
    let totalAlun = 0;
    let totalColab = 0;

    valueTypes.forEach((type, index) => {
      if (index === 0) return;
      if (type === 'colaboradores') {
        totalColab += periodo[type] ?? 0;
      } else {
        totalProf += periodo[`${type}Prof`] ?? 0;
        totalAlun += periodo[`${type}Alun`] ?? 0;
      }
    });
    return {
      professores: totalProf,
      alunos: totalAlun,
      colaboradores: totalColab,
    };
  }, [periodo, getValueTypes]);

  const totais = getTotalByType();

  const toEdit = useCallback(() => {
    openModalFormDados({
      key: "editar",
      tipoPrograma: TipoPrograma.CooperativaEscolar,
      id: periodo.idEscola,
      periodo: periodo.periodo,
      idPeriodo: periodo.id,
      onRefresh,
    });
  }, [openModalFormDados, periodo, onRefresh]);

  const duplicar = useCallback(() => {
    openModalFormDados({
      key: "duplicar",
      tipoPrograma: TipoPrograma.CooperativaEscolar,
      id: periodo.idEscola,
      periodo: periodo.periodo,
      idPeriodo: periodo.id,
      onRefresh,
    });
  }, [openModalFormDados, periodo, onRefresh]);

  const removing = useCallback(() => {
    dispatch(
      removePeriodoByIdCoopetiva(
        periodo.id,
        periodo.idEscola,
        () => {
          SnackbarUtils.enqueueSnackbar({
            message: languageValues.successRemovePeriodo,
            options: { variant: 'success' },
          });
          dispatch(loadDadosEscolaByIdCoop(Number(periodo.idEscola), paginaAtual));
        },
        (error: any) => {
          SnackbarUtils.enqueueSnackbar({
            message: error.response?.statusText || languageValues.errorRemovePeriodo,
            options: { variant: 'error' },
          });
        }
      )
    );
  }, [dispatch, periodo.id, periodo.idEscola, languageValues.successRemovePeriodo, languageValues.errorRemovePeriodo, paginaAtual]);

  const toRemove = useCallback(() => {
    dispatch(
      openModal(ModalAviso, {
        modalKey: 'modal-remover-programa',
        title: languageValues.removerDadosDaEscolaTitle,
        cancelCallback: () => "",
        confirmCallback: () => { removing() },
        confirmLabel: languageValues.removerDadosDaEscolaModalConfirmLabel,
        cancelLabel: 'cancelar',
        content: (
          <Fragment>
            {languageValues.removerDadosDaEscolaModalDescription
              .split('${1}')
              .join(periodo.periodo.split('-').join('/'))}
          </Fragment>
        ),
        confirmThemeButton: 'button_PE',
        cancelThemeButton: 'button_PE',
        tipoPrograma: TipoPrograma.CooperativaEscolar,
        ano: periodo.ano,
        idEscola: periodo.idEscola,
      })
    );
  }, [dispatch,
    languageValues.removerDadosDaEscolaModalConfirmLabel,
    languageValues.removerDadosDaEscolaModalDescription,
    languageValues.removerDadosDaEscolaTitle,
    periodo.ano,
    periodo.idEscola,
    periodo.periodo,
    removing]);

  const allowChange = periodo.permiteEdicao;
  const allowDelete = periodo.permiteExclusao;

  return (
    <Fragment>
      <TableRow
        id={getElementID(id, 'row')}
        className={`${rootClassName}-periodo-row`}
      >
        <TableCell className={getMergedClassNames([
          `${classeCondicional}`,
          `${rootClassName}-cell`,
          `${rootClassName}-action-buttons-cell`,
        ])}>
          {periodo.periodo}
        </TableCell>
        <TableCell className={getMergedClassNames([
          `${classeCondicional}`,
          `${rootClassName}-cell`,
          `${rootClassName}-action-buttons-cell`,
        ])}>
          {totais.colaboradores}
        </TableCell>
        <TableCell className={getMergedClassNames([
          `${classeCondicional}`,
          `${rootClassName}-cell`,
          `${rootClassName}-action-buttons-cell`,
        ])}>
          {totais.professores}
        </TableCell>
        <TableCell className={getMergedClassNames([
          `${classeCondicional}`,
          `${rootClassName}-cell`,
          `${rootClassName}-action-buttons-cell`,
        ])}>
          {totais.alunos}
        </TableCell>
        <TableCell className={getMergedClassNames([
          `${rootClassName}-cell`,
          `${rootClassName}-action-buttons-cell`,
        ])}>
          <div>
            <IconButton onClick={(e) => {
              e.stopPropagation();
              handleMenuClick(e);
            }}>
              <MoreVertIcon />
            </IconButton>

            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                toggleOpen();
              }}
              aria-expanded={opened}
            >
              <AccordionArrowOpenClose
                className={`accordion-icon ${opened ? 'opened' : ''}`}
                style={{ fill: '#33820D' }}
              />
            </IconButton>

          </div>
        </TableCell>

        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
        >
          <MenuItem
            className='menu-item' disabled={!allowChange || isDesabilitado} onClick={() => {
              toEdit()
              handleMenuClose();
            }}>
            Editar
          </MenuItem>
          <MenuItem className='menu-item' disabled={desabilitarDuplicar || isDesabilitado} onClick={() => {
            duplicar()
            handleMenuClose();
          }}>
            Duplicar
          </MenuItem>
          <MenuItem className='menu-item' disabled={!allowDelete || isDesabilitado} onClick={() => {
            toRemove()
            handleMenuClose();
          }}>
            Excluir
          </MenuItem>
        </Menu>
      </TableRow>

      {opened && getValueTypes().map((type, index) => {
        if (index === 0) return null;

        const isLast = index >= getValueTypes().length - 1;
        const noBorderClass = isLast ? '' : `${rootClassName}-no-border`;

        const labelCellClass = `${classeCondicional} ${rootClassName}-cell ${rootClassName}-label-cell ${noBorderClass}`;
        const valueCellClass = `${classeCondicional} ${rootClassName}-cell ${noBorderClass}`;
        const emptyCellClass = `${rootClassName}-cell ${noBorderClass}`;
        const lastEmptyCellClass = `${rootClassName}-cell ${rootClassName}-no-border`;

        return (
          <TableRow className={`${rootClassName}-periodo-sub-row`} key={index}>
            <TableCell className={labelCellClass} colSpan={2}>
              {getLabels()[index]}
            </TableCell>
            <TableCell className={valueCellClass}>
              {periodo[`${type}Prof`]}
            </TableCell>
            <TableCell className={valueCellClass}>
              {periodo[`${type}Alun`]}
            </TableCell>
            <TableCell className={emptyCellClass} />
            <TableCell className={emptyCellClass} />
            <TableCell className={lastEmptyCellClass} />
          </TableRow>
        );
      })}
    </Fragment>
  )
}
export default ListaItem;


